import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Moon, Heart, Users, Activity, ChevronRight, Phone, Sparkles, Leaf, Star, BookOpen, Download, FileText, Stethoscope, Shield } from 'lucide-react';
import { WELLNESS_BENEFITS, CONTACT, BUYERS_GUIDES } from '../data/constants';

const iconMap = {
  Droplets,
  Moon,
  Heart,
  Users,
  Activity,
};

// Wellness benefit images
const BENEFIT_IMAGES = {
  hydrotherapy: 'https://images.unsplash.com/photo-1544843776-7c98a52e08a4?w=800&q=80',
  sleep: 'https://images.pexels.com/photos/6941126/pexels-photo-6941126.jpeg?auto=compress&w=800',
  stress: 'https://customer-assets.emergentagent.com/job_sauna-spa-hub/artifacts/20rgr2kf_HP20-2020-SERHT-5900-Jodie-Hydrotherapy-Zone-Three-Seating-Black-Image2-scaled.jpg',
  family: 'https://images.pexels.com/photos/3851263/pexels-photo-3851263.jpeg?auto=compress&w=800',
  arthritis: 'https://images.pexels.com/photos/7224672/pexels-photo-7224672.jpeg?auto=compress&w=800',
};

// Additional wellness images
const GALLERY_IMAGES = [
  {
    src: 'https://b4087952.smushcdn.com/4087952/wp-content/uploads/2025/06/GR_Muskegon_2_White_HighAngle_Water.jpg?lossy=2&strip=1&webp=1',
    alt: 'Hot tub with water jets',
    caption: 'Powerful hydrotherapy jets'
  },
  {
    src: 'https://b4087952.smushcdn.com/4087952/wp-content/uploads/2025/06/GR_Muskegeon2_Lifestyle_4.jpg?lossy=2&strip=1&webp=1',
    alt: 'Outdoor hot tub lifestyle',
    caption: 'Your backyard oasis'
  },
  {
    src: 'https://images.pexels.com/photos/33405520/pexels-photo-33405520.jpeg?auto=compress&w=800',
    alt: 'Peaceful hot tub setting',
    caption: 'Connect with nature'
  },
];

const WellnessPage = () => {
  return (
    <>
      <Helmet>
        <title>Wellness Benefits of Hot Tubs & Saunas | Leisure Wellness Naples</title>
        <meta name="description" content="Discover the science-backed wellness benefits of hot tubs, saunas, and cold plunges - hydrotherapy, better sleep, stress relief, muscle recovery and more, in Naples, FL." />
        <meta name="keywords" content="wellness benefits, hydrotherapy benefits, hot tub benefits, sauna benefits, cold plunge benefits, naples wellness" />
        <meta property="og:title" content="Wellness Benefits of Hot Tubs, Saunas & Cold Plunges" />
        <meta property="og:description" content="Science-backed wellness benefits of hot tubs, saunas and cold plunges in Naples, FL." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.leisurewellnessnaples.com/wellness" />
      </Helmet>
    <div className="pt-36 pb-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)' }} data-testid="wellness-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden mb-20">
        <div className="absolute inset-0">
          <img 
            src="https://b4087952.smushcdn.com/4087952/wp-content/uploads/2025/06/GR_Muskegon_2_White_HighAngle_Water.jpg?lossy=2&strip=1&webp=1"
            alt="Hot tub wellness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E3D95]/70 via-[#0E3D95]/50 to-[#0E3D95]/80" />
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 mb-4"
          >
            <Sparkles className="text-[#D4AF37]" size={24} />
            <span className="text-sm uppercase tracking-widest text-[#D4AF37] font-semibold">Wellness & Relaxation</span>
            <Sparkles className="text-[#D4AF37]" size={24} />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold uppercase mb-6"
          >
            Live Your <span className="text-[#F88200]">Best Life</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto mb-8"
          >
            Life throws all sorts of hurdles at us—muscle aches, stiff joints, and the general chaos of everyday life. 
            Discover how a hot tub can help you feel better, ease your aches, and truly relax.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Link to="/hot-tubs" className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4">
              Explore Hot Tubs <ChevronRight size={20} />
            </Link>
          </motion.div>
        </div>
        
{/* Scroll indicator removed as per user request */}
      </section>

      {/* === Wellness Experts Callout - new big-font health message === */}
      <section className="px-4 mb-16 md:mb-24" data-testid="wellness-experts-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto bg-gradient-to-br from-[#0E3D95] via-[#0E3D95] to-[#1a4d8a] text-white rounded-2xl shadow-2xl p-8 md:p-14 relative overflow-hidden"
        >
          <div className="absolute -top-12 -right-12 w-72 h-72 bg-[#F88200]/30 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-teal-400/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 text-center">
            <div className="inline-flex items-center gap-2 bg-[#F88200] text-white px-4 py-2 rounded-full text-xs md:text-sm font-bold uppercase tracking-widest mb-6">
              <Stethoscope size={16} /> Your Health is Our Goal
            </div>

            <h2 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black uppercase leading-[0.95] mb-6">
              We Are Your
              <br />
              <span className="text-[#F88200]">Wellness Experts</span>
            </h2>

            <p className="text-xl md:text-3xl font-semibold text-white/90 max-w-4xl mx-auto mb-4">
              Better sleep. Lower stress. Stronger recovery. Healthier numbers.
            </p>
            <p className="text-base md:text-xl text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed">
              A hot tub, sauna, or cold plunge isn&apos;t a luxury &mdash; it&apos;s a daily wellness tool.
              Hydrotherapy is clinically linked to improved circulation, better blood pressure, faster muscle recovery,
              reduced inflammation, and deeper sleep. Come talk to us. We&apos;ll help you build a routine that actually moves the needle on your health.
            </p>

            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl p-6 md:p-8 max-w-3xl mx-auto mb-8">
              <p className="font-['Barlow_Condensed'] text-2xl md:text-4xl font-bold uppercase leading-tight">
                Call Us for Your
                <br className="md:hidden" />
                <span className="text-[#FFD700]"> Wellness Appointment</span>
              </p>
              <a
                href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center gap-3 mt-4 bg-[#F88200] hover:bg-[#e07600] text-white font-black text-3xl md:text-5xl px-8 py-4 rounded-xl shadow-xl transition-all hover:scale-105"
                data-testid="wellness-experts-phone"
              >
                <Phone className="w-7 h-7 md:w-10 md:h-10" /> {CONTACT.phone}
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-[#0E3D95] font-['Barlow_Condensed'] font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Schedule Your Visit <ChevronRight size={18} />
              </Link>
              <a
                href="#buyers-guides"
                onClick={(e) => { e.preventDefault(); document.getElementById('buyers-guides')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 border-2 border-white text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-white/10 transition-colors"
              >
                <BookOpen size={18} /> Free Buyers Guides
              </a>
            </div>
          </div>
        </motion.div>

        {/* Why Trust Us strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mt-6 grid sm:grid-cols-3 gap-4"
        >
          {[
            { icon: Heart, title: 'Health-First Advice', text: 'We sell wellness outcomes, not just hot tubs.' },
            { icon: Shield, title: '20+ Years Experience', text: 'Trusted local experts in hydrotherapy & sauna therapy.' },
            { icon: Sparkles, title: 'Try Before You Buy', text: 'Test the products in our showroom &mdash; no pressure.' },
          ].map((item, i) => (
            <div key={i} className="bg-white/80 backdrop-blur-sm border border-emerald-200 rounded-xl p-5 flex items-start gap-3 shadow-sm">
              <div className="w-12 h-12 bg-[#F88200] rounded-full flex items-center justify-center flex-shrink-0">
                <item.icon className="text-white" size={22} />
              </div>
              <div>
                <p className="font-bold text-[#0E3D95]">{item.title}</p>
                <p className="text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: item.text }} />
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* === Free Buyers Guides === */}
      <section id="buyers-guides" className="px-4 mb-16 md:mb-24" data-testid="wellness-buyers-guides">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-xs md:text-sm font-bold uppercase tracking-widest mb-3">
              <BookOpen size={16} /> Free PDF Downloads
            </div>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold uppercase text-[#0E3D95] mb-3">
              Free Buyers Guides
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Written by the wellness experts at Leisure Wellness Naples. No email required, no spam &mdash; just real info to help you choose what&apos;s right for your body.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Hot Tub Guide */}
            <motion.a
              href={BUYERS_GUIDES.hotTub}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -4 }}
              className="bg-gradient-to-br from-[#0E3D95] to-[#1a4d8a] text-white rounded-xl p-8 shadow-xl flex flex-col gap-4 group"
              data-testid="download-hot-tub-guide"
            >
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white/15 rounded-lg flex items-center justify-center">
                  <FileText className="text-white" size={28} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/70 font-bold">Buyers Guide</p>
                  <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase">Hot Tub Buyers Guide</h3>
                </div>
              </div>
              <p className="text-white/90">
                Everything you need to choose the right hot tub: jet types, sizing, energy use, water care, warranties, and the health benefits of hydrotherapy.
              </p>
              <span className="inline-flex items-center gap-2 mt-auto bg-[#F88200] group-hover:bg-[#e07600] text-white font-bold py-3 px-5 rounded-lg w-fit transition-colors">
                <Download size={18} /> Download Hot Tub Guide
              </span>
            </motion.a>

            {/* Sauna Guide */}
            <motion.a
              href={BUYERS_GUIDES.sauna}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -4 }}
              className="bg-gradient-to-br from-[#F88200] to-[#e07600] text-white rounded-xl p-8 shadow-xl flex flex-col gap-4 group"
              data-testid="download-sauna-guide"
            >
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white/15 rounded-lg flex items-center justify-center">
                  <FileText className="text-white" size={28} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/80 font-bold">Buyers Guide</p>
                  <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase">Sauna Buyers Guide</h3>
                </div>
              </div>
              <p className="text-white/90">
                Traditional vs. infrared vs. hybrid, wood types, heater quality, EMF, indoor vs. outdoor placement, and how to build a sustainable sauna routine.
              </p>
              <span className="inline-flex items-center gap-2 mt-auto bg-white text-[#F88200] font-bold py-3 px-5 rounded-lg w-fit group-hover:bg-slate-100 transition-colors">
                <Download size={18} /> Download Sauna Guide
              </span>
            </motion.a>
          </div>
        </motion.div>
      </section>
      
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Intro Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0E3D95] mb-6">
            The Healing Power of Warm Water
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            For centuries, people have turned to the soothing comfort of warm water to calm their aches and stress. 
            From the lavish baths of ancient Rome to the cozy hot tubs we love today, a warm soak has the power to relax both body and mind.
          </p>
        </motion.div>
        
        {/* Health Benefits Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
          data-testid="health-benefits-video"
        >
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-center text-[#0E3D95] mb-8">
            Health Benefits of Hot Tubs
          </h2>
          <div className="max-w-4xl mx-auto aspect-video rounded-lg overflow-hidden shadow-2xl">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              controls
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/0eqytheo_Leisure%20Wellness%20Naples%20%E2%80%94%20Play%20Hard%2C%20Recover%20Harder-2.mp4" type="video/mp4" />
            </video>
          </div>
        </motion.div>
        
        {/* Image Gallery */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-4 mb-24"
        >
          {GALLERY_IMAGES.map((img, idx) => (
            <motion.div 
              key={img.src}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="relative group overflow-hidden aspect-[4/3]"
            >
              <img 
                src={img.src} 
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E3D95]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <p className="text-white font-semibold">{img.caption}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Benefits Grid */}
        <div className="space-y-32 mb-24">
          {WELLNESS_BENEFITS.map((benefit, idx) => {
            const Icon = iconMap[benefit.icon] || Heart;
            const isEven = idx % 2 === 0;
            const benefitImage = BENEFIT_IMAGES[benefit.id];
            
            return (
              <motion.div
                key={benefit.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`grid md:grid-cols-2 gap-12 items-center`}
              >
                <div className={isEven ? 'md:order-1' : 'md:order-2'}>
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      className="w-16 h-16 bg-[#F88200] rounded-full flex items-center justify-center shadow-lg"
                    >
                      <Icon className="text-white" size={28} />
                    </motion.div>
                    <h3 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0E3D95]">
                      {benefit.title}
                    </h3>
                  </div>
                  <p className="text-lg text-slate-600 leading-relaxed mb-6">
                    {benefit.description}
                  </p>
                  <ul className="space-y-3">
                    {getBenefitPoints(benefit.id).map((point, i) => (
                      <li key={`benefit-${benefit.id}-point-${i}`} className="flex items-start gap-3">
                        <Leaf className="text-green-600 flex-shrink-0 mt-1" size={18} />
                        <span className="text-slate-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`relative overflow-hidden rounded-lg shadow-2xl ${isEven ? 'md:order-2' : 'md:order-1'}`}>
                  {benefitImage ? (
                    <img 
                      src={benefitImage}
                      alt={benefit.title}
                      className="w-full h-[400px] object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-[400px] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                      <Icon className="text-[#F88200]/30" size={120} />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#0E3D95]/50 to-transparent" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Hot Tub & Sleep Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-center text-[#0E3D95] mb-4">
            Hot Tubs & Better Sleep
          </h2>
          <p className="text-lg text-slate-600 text-center max-w-3xl mx-auto mb-8">
            Discover how a warm soak before bed can help you achieve deeper, more restful sleep.
          </p>
          <div className="max-w-4xl mx-auto aspect-video rounded-lg overflow-hidden shadow-2xl">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              controls
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_wet-test-preview/artifacts/ru0cb4f1_HOT%20TUBBER%20SLEEP.mp4" type="video/mp4" />
            </video>
          </div>
        </motion.div>

        {/* Leisure Wellness Naples Lifestyle Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-center text-[#0E3D95] mb-4">
            Experience the Leisure Wellness Naples Lifestyle
          </h2>
          <p className="text-lg text-slate-600 text-center max-w-3xl mx-auto mb-8">
            See how our customers in Naples and Southwest Florida enjoy their hot tubs, saunas, cold plunges, and swim spas.
          </p>
          <div className="max-w-4xl mx-auto aspect-video rounded-lg overflow-hidden shadow-2xl">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              controls
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/0eqytheo_Leisure%20Wellness%20Naples%20%E2%80%94%20Play%20Hard%2C%20Recover%20Harder-2.mp4" type="video/mp4" />
            </video>
          </div>
        </motion.div>
        
        {/* Stats Section */}
        <motion.section 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-[#0E3D95] text-white py-20 px-8 mb-24 -mx-4 md:-mx-8 relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-[#F88200] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          </div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-center mb-4">
              The Science Behind <span className="text-[#D4AF37]">Hydrotherapy</span>
            </h2>
            <p className="text-center text-slate-400 mb-12 max-w-2xl mx-auto">
              Research shows that regular hot tub use can provide significant health benefits
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { value: '37°C', label: 'Optimal Water Temp', sublabel: 'For therapeutic benefits' },
                { value: '15-20', label: 'Minutes for Relief', sublabel: 'Per session recommended' },
                { value: '40%', label: 'Stress Reduction', sublabel: 'Reported by users' },
                { value: '100%', label: 'Made in America', sublabel: 'Quality you can trust' },
              ].map((stat, idx) => (
                <motion.div 
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <p className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-[#D4AF37] mb-2">
                    {stat.value}
                  </p>
                  <p className="text-white font-semibold mb-1">{stat.label}</p>
                  <p className="text-slate-500 text-sm">{stat.sublabel}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
        
        {/* Testimonials */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-center text-[#0E3D95] mb-12">
            What Our Customers Say
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                quote: "Since getting our hot tub, my chronic back pain has improved significantly. The nightly soaks have become our family's favorite way to unwind together.",
                author: "Sarah M.",
                location: "Naples, FL",
                rating: 5
              },
              {
                quote: "I was skeptical at first, but the hydrotherapy jets have done wonders for my arthritis. I can't imagine life without our hot tub now!",
                author: "Robert T.",
                location: "Fort Myers, FL",
                rating: 5
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={`testimonial-${testimonial.author}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 p-8 relative"
              >
                <div className="absolute top-6 left-6 text-6xl text-[#F88200]/10 font-serif">"</div>
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`star-${testimonial.author}-${i}`} className="text-[#D4AF37] fill-[#D4AF37]" size={20} />
                  ))}
                </div>
                <blockquote className="text-lg text-slate-700 italic mb-6 relative z-10">
                  {testimonial.quote}
                </blockquote>
                <div>
                  <p className="font-semibold text-[#0E3D95]">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
        
        {/* Feature Grid */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-center text-[#0E3D95] mb-12">
            Why Choose Hydrotherapy?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Droplets, title: 'Joint Relief', desc: 'Buoyancy reduces pressure on joints by up to 90%' },
              { icon: Heart, title: 'Better Circulation', desc: 'Warm water dilates blood vessels for improved flow' },
              { icon: Moon, title: 'Quality Sleep', desc: 'Evening soaks prepare your body for deeper rest' },
              { icon: Activity, title: 'Muscle Recovery', desc: 'Heat and massage accelerate healing after exercise' },
            ].map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white border border-slate-200 p-6 text-center hover:border-[#F88200] hover:shadow-lg transition-all"
              >
                <feature.icon className="text-[#F88200] mx-auto mb-4" size={40} />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0E3D95] mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
        
        {/* Dive Deeper Into Wellness Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <BookOpen className="text-[#F88200]" size={24} />
              <span className="text-sm uppercase tracking-widest text-[#F88200] font-semibold">Learn More</span>
            </div>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0E3D95] mb-4">
              Dive Deeper Into Wellness
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Explore the science behind hot tub therapy and discover how different technologies work together to enhance your well-being.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link 
              to="/anatomy-of-a-spa"
              className="bg-white p-8 shadow-lg hover:shadow-xl transition-all group border-l-4 border-[#F88200]"
            >
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-3 group-hover:text-[#F88200] transition-colors">
                Anatomy of a Spa
              </h3>
              <p className="text-slate-600 mb-4">
                Learn about hot tub components and how they work together to provide the ultimate therapeutic experience.
              </p>
              <span className="inline-flex items-center gap-2 text-[#F88200] font-semibold">
                Explore Components <ChevronRight size={18} />
              </span>
            </Link>
            
            <Link 
              to="/balneotherapy"
              className="bg-white p-8 shadow-lg hover:shadow-xl transition-all group border-l-4 border-[#0E3D95]"
            >
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-3 group-hover:text-[#F88200] transition-colors">
                Balneotherapy
              </h3>
              <p className="text-slate-600 mb-4">
                Discover the ancient science of water therapy and its proven health benefits backed by centuries of practice.
              </p>
              <span className="inline-flex items-center gap-2 text-[#F88200] font-semibold">
                Learn the Science <ChevronRight size={18} />
              </span>
            </Link>
            
            <Link 
              to="/jets"
              className="bg-white p-8 shadow-lg hover:shadow-xl transition-all group border-l-4 border-[#D4AF37]"
            >
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-3 group-hover:text-[#F88200] transition-colors">
                Jet Technology
              </h3>
              <p className="text-slate-600 mb-4">
                Understand different jet types and how they target specific muscle groups for maximum therapeutic relief.
              </p>
              <span className="inline-flex items-center gap-2 text-[#F88200] font-semibold">
                View Jet Types <ChevronRight size={18} />
              </span>
            </Link>
          </div>
        </motion.section>
        
        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-[#F88200] to-[#7F1D1D] text-white p-12 -mx-4 md:-mx-8"
        >
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold mb-4">
            Start Your Wellness Journey Today
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Visit our showroom and experience the benefits of hydrotherapy firsthand with a complimentary "Wet Test." 
            We have robes, towels, and slippers ready for you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/hot-tubs" 
              className="bg-white text-[#F88200] font-['Barlow_Condensed'] font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors shadow-lg"
              data-testid="shop-hot-tubs-btn"
            >
              Shop Hot Tubs <ChevronRight size={18} />
            </Link>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
              data-testid="schedule-visit-btn"
            >
              Schedule a Visit
            </Link>
            <a 
              href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`}
              className="border-2 border-white/50 text-white font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 hover:border-white hover:bg-white/10 transition-colors"
            >
              <Phone size={18} /> {CONTACT.phone}
            </a>
          </div>
        </motion.section>
      </div>
    </div>
    </>
  );
};

// Helper function to get benefit points
const getBenefitPoints = (id) => {
  const points = {
    hydrotherapy: [
      'Improves blood circulation throughout the body',
      'Relieves muscle tension and soreness',
      'Reduces inflammation and swelling',
    ],
    sleep: [
      'Body temperature drop after soaking promotes drowsiness',
      'Reduces anxiety that can interfere with sleep',
      'Creates a relaxing bedtime routine',
    ],
    stress: [
      'Triggers release of endorphins (natural mood boosters)',
      'Lowers cortisol levels in the body',
      'Provides dedicated time for mindfulness and relaxation',
    ],
    family: [
      'Screen-free environment for meaningful conversations',
      'Creates lasting memories together',
      'Teaches children the value of relaxation',
    ],
    arthritis: [
      'Warm water soothes stiff, painful joints',
      'Buoyancy takes pressure off joints',
      'Regular use can improve flexibility and range of motion',
    ],
  };
  return points[id] || [];
};

export default WellnessPage;
